import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    Avatar,
    Card,
    CardContent,
    Chip,
    Stack,
    useTheme,
    alpha
} from '@mui/material';
import { 
    Visibility, 
    VisibilityOff, 
    AccountCircle, 
    Security, 
    Email, 
    Phone,
    Edit as EditIcon,
    VpnKey as KeyIcon,
    Badge as BadgeIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import scanApi from '../../services/api';
import { generatePassword } from '../../utils/passwordGenerator';

const ProfileManager = () => {
    const theme = useTheme();
    const { user, refreshUserInfo } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (user) {
            setFormData(prev => ({
                ...prev,
                name: user.name || '',
                surname: user.surname || '',
                email: user.email || '',
                phone: user.phone || ''
            }));
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (formData.newPassword) {
                if (formData.newPassword !== formData.confirmPassword) {
                    throw new Error('Le nuove password non corrispondono');
                }
                if (!formData.currentPassword) {
                    throw new Error('La password attuale è richiesta per impostare una nuova password');
                }

                const response = await scanApi.updatePassword({
                    currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                });
                
                if (response.success) {
                    setSuccess('Password aggiornata con successo');
                    setFormData(prev => ({
                        ...prev,
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                    }));
                }
            }
        } catch (error) {
            console.error('Errore durante l\'aggiornamento:', error);
            setError(error.response?.data?.error || error.message || 'Errore durante l\'aggiornamento della password');
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePassword = () => {
        const newPassword = generatePassword();
        setFormData(prev => ({
            ...prev,
            newPassword,
            confirmPassword: newPassword
        }));
    };

    // Funzione per mostrare l'errore o il successo
    const renderAlert = () => {
        if (error) {
            return (
                <Alert 
                    severity="error" 
                    onClose={() => setError(null)}
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.1)
                    }}
                >
                    {error}
                </Alert>
            );
        }
        
        if (success) {
            return (
                <Alert 
                    severity="success"
                    onClose={() => setSuccess(null)}
                    sx={{ 
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1)
                    }}
                >
                    {success}
                </Alert>
            );
        }
        
        return null;
    };

    return (
        <Box 
            sx={{ 
                p: 4,
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
            }}
        >
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            mb: 4,
                            fontWeight: 700,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textAlign: 'center'
                        }}
                    >
                        Gestione Profilo
                    </Typography>
                </Grid>

                {/* Profilo Card */}
                <Grid item xs={12} md={4}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            height: '100%',
                            bgcolor: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 4,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }}
                    >
                        <CardContent>
                            <Stack spacing={4} alignItems="center">
                                <Box
                                    sx={{
                                        position: 'relative',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -2,
                                            left: -2,
                                            right: -2,
                                            bottom: -2,
                                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                            borderRadius: '50%',
                                            zIndex: 0
                                        }
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            bgcolor: theme.palette.background.paper,
                                            fontSize: '3rem',
                                            fontWeight: 700,
                                            border: '4px solid',
                                            borderColor: 'background.paper',
                                            position: 'relative',
                                            zIndex: 1
                                        }}
                                    >
                                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                    </Avatar>
                                </Box>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="h5" 
                                        gutterBottom
                                        sx={{ 
                                            fontWeight: 600,
                                            color: 'text.primary'
                                        }}
                                    >
                                        {user?.name} {user?.surname}
                                    </Typography>
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <Chip 
                                            icon={<BusinessIcon />}
                                            label={user?.role || 'Utente'} 
                                            color="primary" 
                                            variant="outlined"
                                            sx={{ 
                                                borderRadius: 2,
                                                '& .MuiChip-icon': { 
                                                    color: theme.palette.primary.main 
                                                }
                                            }}
                                        />
                                        <Chip 
                                            icon={<BadgeIcon />}
                                            label={user?.status || 'Attivo'} 
                                            color="success" 
                                            variant="outlined"
                                            sx={{ 
                                                borderRadius: 2,
                                                '& .MuiChip-icon': { 
                                                    color: theme.palette.success.main 
                                                }
                                            }}
                                        />
                                    </Stack>
                                </Box>

                                <Box 
                                    sx={{ 
                                        width: '100%',
                                        p: 2,
                                        bgcolor: alpha(theme.palette.background.default, 0.4),
                                        borderRadius: 2
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 2,
                                            p: 1.5,
                                            bgcolor: alpha(theme.palette.background.paper, 0.4),
                                            borderRadius: 2
                                        }}>
                                            <Email sx={{ color: theme.palette.primary.main }} />
                                            <Typography variant="body2">{user?.email}</Typography>
                                        </Box>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 2,
                                            p: 1.5,
                                            bgcolor: alpha(theme.palette.background.paper, 0.4),
                                            borderRadius: 2
                                        }}>
                                            <Phone sx={{ color: theme.palette.primary.main }} />
                                            <Typography variant="body2">{user?.phone || 'Non specificato'}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Form di Modifica */}
                <Grid item xs={12} md={8}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            p: 4,
                            bgcolor: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(20px)',
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 4
                        }}
                    >
                        <Box sx={{ mb: 4 }}>
                            <Typography 
                                variant="h5" 
                                gutterBottom 
                                sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    color: theme.palette.text.primary,
                                    fontWeight: 600
                                }}
                            >
                                <EditIcon sx={{ color: theme.palette.primary.main }} />
                                Modifica Profilo
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Gestisci le tue informazioni personali e le impostazioni di sicurezza
                            </Typography>
                        </Box>
                        
                        {renderAlert()}

                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nome"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cognome"
                                        name="surname"
                                        value={formData.surname}
                                        onChange={handleChange}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Telefono"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 3 }}>
                                        <Chip 
                                            icon={<KeyIcon />}
                                            label="Sicurezza" 
                                            color="primary"
                                            sx={{ px: 2 }}
                                        />
                                    </Divider>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Password Attuale"
                                        name="currentPassword"
                                        type="password"
                                        value={formData.currentPassword}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Security sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nuova Password"
                                        name="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.newPassword}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Security sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Conferma Nuova Password"
                                        name="confirmPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Security sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: alpha(theme.palette.background.paper, 0.6)
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleGeneratePassword}
                                            startIcon={<Security />}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                px: 3
                                            }}
                                        >
                                            Genera Password Sicura
                                        </Button>

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={loading}
                                            sx={{ 
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                px: 4,
                                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                '&:hover': {
                                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                                                }
                                            }}
                                        >
                                            {loading ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Salva Modifiche'
                                            )}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfileManager;
